import { Signal } from '@angular/core';
import { SizeClass } from '@medinbox/angular';
import { CASE_ZONES, DEFAULT_ZONES } from '@shared/constants';
import { CaseZone, RowZone, TabState, TabsState, ZoneRowRules } from '@shared/models';
import { inHalves } from '@shared/utils';

/** The case details row: the report only on a case that has one, the contents drawn inside the chat. */
export function caseZoneRules(reportAvailable: Signal<boolean>): ZoneRowRules<CaseZone, TabsState> {
  return {
    zones: CASE_ZONES,
    offers: (zone) => zone !== 'report' || reportAvailable(),
    stateOf: zonesToTabState,
    visibleIn,
    drawn(stored, sizeClass) {
      const tabs = reportAvailable() ? stored : withoutReport(stored);
      if (sizeClass === 'medium') {
        return inCaseHalves(tabs);
      }
      // Drawn inside the chat, the contents cannot hold once it is hidden.
      return tabs.chat === 'minimized' ? { ...tabs, content: 'closed' } : tabs;
    },
    picked: (stored, drawn, zones, sizeClass) =>
      picked(stored, drawn, zones, sizeClass, reportAvailable()),
    opened: (stored, _zone, sizeClass) => opened(stored, sizeClass),
    closed: (stored) => ({ ...stored, content: 'closed' }),
  };
}

/**
 * Spreads the zones of the row over their tab states: a zone left alone takes the row, and a hidden
 * zone is minimized rather than removed so the panels keep their transition.
 */
export function spreadRow(
  shown: Record<RowZone, boolean>,
  content: TabsState['content'],
): TabsState {
  const alone = Object.values(shown).filter(Boolean).length === 1;
  const stateOf = (zone: RowZone): TabState =>
    shown[zone] ? (alone ? 'fullscreen' : 'default') : 'minimized';
  return { video: stateOf('video'), chat: stateOf('chat'), report: stateOf('report'), content };
}

export function zonesToTabState(zones: readonly CaseZone[]): TabsState {
  const chat = zones.includes('chat');
  return spreadRow(
    { video: zones.includes('video'), chat, report: zones.includes('report') },
    // The panel is drawn inside the chat, so a stored pair without the chat cannot hold.
    chat && zones.includes('content') ? 'open' : 'closed',
  );
}

/** The zones a layout shows, in {@link CASE_ZONES} order. */
export function visibleIn(tabs: TabsState): CaseZone[] {
  return CASE_ZONES.filter((zone) =>
    zone === 'content' ? tabs.content === 'open' : tabs[zone] !== 'minimized',
  );
}

/** The layout without the report, for a case that has none: its room goes back to the others. */
export function withoutReport(tabs: TabsState): TabsState {
  if (tabs.report === 'minimized') {
    return tabs;
  }
  const video = tabs.video !== 'minimized';
  const chat = tabs.chat !== 'minimized';
  // The report was alone on the row: the case opens as a first visit does.
  return video || chat
    ? spreadRow({ video, chat, report: false }, tabs.content)
    : zonesToTabState(DEFAULT_ZONES);
}

/**
 * In halves: the chat or the report on the left, the video or the generated contents on the right.
 * The zones under the contents come back when they close.
 */
export function inCaseHalves(tabs: TabsState): TabsState {
  if (tabs.content === 'open') {
    return { video: 'minimized', chat: 'default', report: 'minimized', content: 'open' };
  }
  const shown = {
    video: tabs.video !== 'minimized',
    chat: tabs.chat !== 'minimized',
    report: tabs.report !== 'minimized',
  };
  return inHalves(shown, 'report').report === shown.report
    ? tabs
    : { ...tabs, report: 'minimized' };
}

function picked(
  tabs: TabsState,
  drawn: TabsState,
  zones: readonly CaseZone[],
  sizeClass: SizeClass,
  reportAvailable: boolean,
): TabsState {
  const phoneFormat = sizeClass !== 'expanded';
  const reportShown = drawn.report !== 'minimized';
  const contentClosed = drawn.content === 'closed';
  const video = zones.includes('video');
  let chat = zones.includes('chat');
  // A case without a report offers no report segment: the choice stays for the next one.
  let report = reportAvailable ? zones.includes('report') : tabs.report !== 'minimized';
  // Medium without the video, the chat or the report just turned on joins the other: chat | report.
  const reportBesideChat = sizeClass === 'medium' && !video;
  // Side by side on a phone the chat and the report share the left half: the one just picked wins.
  if (phoneFormat && reportAvailable && chat && report && !reportBesideChat) {
    chat = reportShown;
    report = !reportShown;
  }
  // Side by side on a phone the contents fill the row: a zone picked in the header puts them away.
  // Expanded, the header has no contents segment and drops them when a zone is added: they stay open.
  let content = !phoneFormat && (zones.includes('content') || !contentClosed);

  // Drawn inside the chat: showing the contents brings the chat back, hiding the chat takes them.
  if (content && contentClosed) {
    chat = true;
  } else if (!chat) {
    content = false;
  }

  const contentState: TabsState['content'] = content ? 'open' : 'closed';

  // The zones share the row: hiding the last one shown would leave it empty.
  if (!video && !chat && !(report && reportAvailable)) {
    return { ...tabs, content: contentState };
  }
  return spreadRow({ video, chat, report }, contentState);
}

/** The contents opened from the page itself: a hidden chat comes back carrying them, the video stays. */
function opened(tabs: TabsState, sizeClass: SizeClass): TabsState {
  if (sizeClass !== 'expanded') {
    return { ...tabs, content: 'open' };
  }
  if (tabs.chat === 'minimized') {
    return spreadRow(
      { video: tabs.video !== 'minimized', chat: true, report: tabs.report !== 'minimized' },
      'open',
    );
  }
  return { ...tabs, content: 'open' };
}
